import { useEffect, useState } from 'react'
import { useLang } from '../i18n/LangProvider'
import { useCatalogoApi } from '../catalogo/api'
import { useAuth } from '../auth/useAuth'
import { t, Lang } from './i18n'
import { t as tCatalogo } from '../catalogo/i18n'
import { PanelDetailDrawer } from './PanelDetailDrawer'
import { PanelEntrenadoresTab } from './PanelEntrenadoresTab'
import { PanelJugadoresTab } from './PanelJugadoresTab'
import { PanelAusenciasTab } from './PanelAusenciasTab'
import { PanelTarjetasTab } from './PanelTarjetasTab'
import styles from './PanelPage.module.css'
import { EmptyState } from '../styles/EmptyState'
import { Icon } from '../styles/Icon'
import { PageTopbar } from '../styles/PageTopbar'
import { formatDateTime, localeFor } from '../utils/format'

interface Temporada {
  id: string
  nombre: string
  estado: 'abierta' | 'cerrada'
}

export interface EquipoEstado {
  id: string
  nombre: string
  categoria: 'eskola' | 'f7' | 'f11'
  asistencia_pendiente: boolean | null
  minutaje_pendiente: boolean | null
  semaforo: 'verde' | 'rojo' | 'sin_datos'
  ultima_actualizacion_asistencia: string | null
  ultima_actualizacion_minutaje: string | null
}

const CATEGORIAS: EquipoEstado['categoria'][] = ['eskola', 'f7', 'f11']
const REFRESH_MS = 60000

function SemaforoIcono({
  semaforo,
  etiquetaBoton,
  onRecordatorio,
}: {
  semaforo: EquipoEstado['semaforo']
  etiquetaBoton?: string
  onRecordatorio?: () => void
}) {
  // Issue #78 (club, 2026-10-22): "el icono rojo de la tarjeta debe funcionar como botón para enviar
  // ese recordatorio manualmente en cualquier momento".
  if (semaforo === 'rojo' && onRecordatorio)
    return (
      <button
        type="button"
        aria-label={`rojo: ${etiquetaBoton}`}
        title={etiquetaBoton}
        className={`${styles.semaforo} ${styles.semaforoRojo} ${styles.semaforoBoton}`}
        onClick={onRecordatorio}
      >
        🔴
      </button>
    )
  if (semaforo === 'rojo')
    return (
      <span aria-label="rojo" className={`${styles.semaforo} ${styles.semaforoRojo}`}>
        🔴
      </span>
    )
  if (semaforo === 'verde')
    return (
      <span aria-label="verde" className={`${styles.semaforo} ${styles.semaforoVerde}`}>
        🟢
      </span>
    )
  return (
    <span aria-label="sin_datos" className={`${styles.semaforo} ${styles.semaforoSinDatos}`}>
      ⚪
    </span>
  )
}

function Chip({ pendiente, lang }: { pendiente: boolean | null; lang: Lang }) {
  if (pendiente === null) return <span className={styles.chip}>—</span>
  return <span className={styles.chip}>{pendiente ? `⚠️ ${t('pendiente', lang)}` : `✅ ${t('alDia', lang)}`}</span>
}

export function PanelPage({ lang: langProp }: { lang?: Lang }) {
  const ctxLang = useLang()
  const lang = langProp ?? ctxLang
  const api = useCatalogoApi()
  const { user, logout } = useAuth()
  const [temporadas, setTemporadas] = useState<Temporada[]>([])
  const [temporadaId, setTemporadaId] = useState('')
  const [equipos, setEquipos] = useState<EquipoEstado[]>([])
  // Feedback de navegación del cliente, 2026-10-02: "entro panel selecciono equipo y dice todo el
  // dia, que es? este contexto q es? no lo entiendo" — el diálogo no tenía ningún título que dijera
  // qué equipo ni qué significa, especialmente cuando no hay nada pendiente.
  const [drawerEquipo, setDrawerEquipo] = useState<{ id: string; nombre: string } | null>(null)
  const [vista, setVista] = useState<'equipos' | 'entrenadores' | 'jugadores' | 'ausencias' | 'tarjetas'>('equipos')
  const locale = localeFor(lang)

  useEffect(() => {
    api.get('/catalogo/temporadas').then((data: Temporada[]) => {
      setTemporadas(data)
      const abiertas = data.filter((temporada) => temporada.estado === 'abierta')
      if (abiertas.length === 1) setTemporadaId(abiertas[0].id)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function refresh() {
    if (!temporadaId) return
    const data: EquipoEstado[] = await api.get(`/panel/estado?temporada_id=${temporadaId}`)
    setEquipos(data)
  }

  useEffect(() => {
    refresh()
    if (!temporadaId) return
    const interval = setInterval(refresh, REFRESH_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporadaId])

  // Issue #78: envío manual del recordatorio (con confirmación, porque manda un email real).
  async function enviarRecordatorio(equipo: { id: string; nombre: string }) {
    if (!window.confirm(t('confirmarRecordatorio', lang).replace('{equipo}', equipo.nombre))) return
    const resultado: { pendientes: number; enviados: string[] } = await api.post(`/panel/recordatorio/${equipo.id}`)
    window.alert(
      resultado.enviados.length > 0
        ? t('recordatorioEnviado', lang).replace('{nombres}', resultado.enviados.join(', '))
        : resultado.pendientes === 0
          ? t('recordatorioSinPendientes', lang)
          : t('recordatorioSinEntrenadores', lang),
    )
  }

  const categoriasPresentes = CATEGORIAS.filter((cat) => equipos.some((e) => e.categoria === cat))

  return (
    <div>
      {/* Feedback de navegación del cliente, 2026-10-02: "no sé quién soy, qué hago, no hay hilo de
          Ariadna" — el Panel era la única pantalla sin ningún indicio de usuario/rol ni forma de
          salir aparte de "Volver" (que lleva al catálogo, no cierra sesión). */}
      <PageTopbar>
        {user && (
          <span className={styles.userBadge}>
            {user.nombre_visible ?? user.email} ({user.roles?.join(', ')})
          </span>
        )}
        <button className={styles.salir} onClick={() => logout()}>
          {tCatalogo('salir', lang)}
        </button>
      </PageTopbar>
      <h2>
        <Icon name="alerta" className="icon-alerta" />
        {t('panel', lang)}
      </h2>

      <nav className="tabs">
        <button onClick={() => setVista('equipos')} disabled={vista === 'equipos'}>
          {t('vistaEquipos', lang)}
        </button>
        <button onClick={() => setVista('entrenadores')} disabled={vista === 'entrenadores'}>
          {t('vistaEntrenadores', lang)}
        </button>
        <button onClick={() => setVista('jugadores')} disabled={vista === 'jugadores'}>
          {t('vistaJugadores', lang)}
        </button>
        {/* Issue #85 (club, 2026-10-23): "En el apartado Alertas, añades dos botones: Ausencias y Tarjetas". */}
        <button onClick={() => setVista('ausencias')} disabled={vista === 'ausencias'}>
          {t('vistaAusencias', lang)}
        </button>
        <button onClick={() => setVista('tarjetas')} disabled={vista === 'tarjetas'}>
          {t('vistaTarjetas', lang)}
        </button>
      </nav>

      {temporadas.length > 0 && !temporadaId && (
        <select value={temporadaId} onChange={(e) => setTemporadaId(e.target.value)}>
          <option value="" disabled>
            {t('seleccionaTemporada', lang)}
          </option>
          {temporadas.map((temporada) => (
            <option key={temporada.id} value={temporada.id}>
              {temporada.nombre}
              {temporada.estado === 'cerrada' ? ` (${t('cerrada', lang)})` : ''}
            </option>
          ))}
        </select>
      )}

      {vista === 'entrenadores' && temporadaId && <PanelEntrenadoresTab temporadaId={temporadaId} lang={lang} />}

      {vista === 'jugadores' && temporadaId && <PanelJugadoresTab equipos={equipos} lang={lang} />}

      {vista === 'ausencias' && temporadaId && <PanelAusenciasTab temporadaId={temporadaId} lang={lang} />}

      {vista === 'tarjetas' && temporadaId && (
        <PanelTarjetasTab temporadaId={temporadaId} lang={lang} hayF11={equipos.some((e) => e.categoria === 'f11')} />
      )}

      {vista === 'equipos' && (
        <>
      {temporadaId && categoriasPresentes.length === 0 && <EmptyState text={t('sinEquipos', lang)} icon="🛡️" />}

      {categoriasPresentes.map((categoria) => (
        <section key={categoria} className={styles.section}>
          <h3>{t(categoria, lang)}</h3>
          <div className={styles.grid}>
            {equipos
              .filter((equipo) => equipo.categoria === categoria)
              .map((equipo) => (
                // Contenedor con dos botones (un botón no puede ir dentro de otro): el semáforo rojo envía el
                // recordatorio (#78) y el resto de la tarjeta abre los pendientes, como antes.
                <div key={equipo.id} className={styles.card}>
                  <SemaforoIcono
                    semaforo={equipo.semaforo}
                    etiquetaBoton={t('enviarRecordatorio', lang)}
                    onRecordatorio={() => enviarRecordatorio(equipo)}
                  />
                  <button className={styles.cardBody} onClick={() => setDrawerEquipo({ id: equipo.id, nombre: equipo.nombre })}>
                  <strong>{equipo.nombre}</strong>
                  <span className={styles.cardCategoria}>{t(categoria, lang)}</span>
                  <div>
                    {t('asistencia', lang)}: <Chip pendiente={equipo.asistencia_pendiente} lang={lang} />
                  </div>
                  <div>
                    {t('minutaje', lang)}: <Chip pendiente={equipo.minutaje_pendiente} lang={lang} />
                  </div>
                  {equipo.ultima_actualizacion_asistencia && (
                    <div>
                      {t('ultimaActualizacion', lang)} ({t('asistencia', lang)}):{' '}
                      {formatDateTime(equipo.ultima_actualizacion_asistencia, locale)}
                    </div>
                  )}
                  {equipo.ultima_actualizacion_minutaje && (
                    <div>
                      {t('ultimaActualizacion', lang)} ({t('minutaje', lang)}):{' '}
                      {formatDateTime(equipo.ultima_actualizacion_minutaje, locale)}
                    </div>
                  )}
                  </button>
                </div>
              ))}
          </div>
        </section>
      ))}
        </>
      )}

      {drawerEquipo && (
        <PanelDetailDrawer
          onRecordatorio={() => enviarRecordatorio(drawerEquipo)}
          equipoId={drawerEquipo.id}
          equipoNombre={drawerEquipo.nombre}
          lang={lang}
          onClose={() => setDrawerEquipo(null)}
        />
      )}
    </div>
  )
}
